import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  getDocs,
  collection,
} from "firebase/firestore";
import { db } from "../../firebase";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import DOMPurify from "dompurify";
import "../solutions/Solutions.css";
import { Helmet } from "react-helmet-async";

const parseDate = (value) => {
  if (!value) return null;

  const date = new Date(`${value}T12:00:00`);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

const formatTradeshowDates = (
  startValue,
  endValue
) => {
  const start = parseDate(startValue);
  const end =
    parseDate(endValue) || start;

  if (!start) {
    return "Dates Coming Soon";
  }

  const startMonth =
    start.toLocaleString("en-US", {
      month: "long",
    });

  const endMonth =
    end.toLocaleString("en-US", {
      month: "long",
    });

  const startDay = start.getDate();
  const endDay = end.getDate();

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  if (
    startDay === endDay &&
    startMonth === endMonth &&
    startYear === endYear
  ) {
    return `${startMonth} ${startDay}, ${startYear}`;
  }

  if (
    startMonth === endMonth &&
    startYear === endYear
  ) {
    return `${startMonth} ${startDay}–${endDay}, ${startYear}`;
  }

  if (startYear === endYear) {
    return `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${startYear}`;
  }

  return `${startMonth} ${startDay}, ${startYear} – ${endMonth} ${endDay}, ${endYear}`;
};

const formatBooth = (booth = "") => {
  const value = booth.trim();

  if (!value) {
    return "Booth: Coming Soon";
  }

  if (/^booth/i.test(value)) {
    return value;
  }

  return `Booth ${value}`;
};

function Tradeshow() {
  const { slug } = useParams();

  const [show, setShow] =
    useState(null);

  const [otherShows, setOtherShows] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [productIndex, setProductIndex] =
    useState(0);

  const [
    productsPerView,
    setProductsPerView,
  ] = useState(3);


  /* ===========================
     LOAD TRADESHOW
     =========================== */

  useEffect(() => {
    const loadShow = async () => {
      setLoading(true);

      try {
        const [
          showSnapshot,
          allShowsSnapshot,
        ] = await Promise.all([
          getDoc(
            doc(
              db,
              "tradeshows",
              slug
            )
          ),

          getDocs(
            collection(
              db,
              "tradeshows"
            )
          ),
        ]);

        if (!showSnapshot.exists()) {
          setShow(null);
          return;
        }

        const showData = {
          id: showSnapshot.id,
          ...showSnapshot.data(),
        };

        setShow(showData);

        /*
          Automatically build the
          "Other upcoming shows" section.
        */

        const today = new Date();
        today.setHours(
          0,
          0,
          0,
          0
        );

        const otherRows =
          allShowsSnapshot.docs
            .map((docSnap) => ({
              id: docSnap.id,
              ...docSnap.data(),
            }))
            .filter((item) => {
              if (
                item.id ===
                showSnapshot.id
              ) {
                return false;
              }

              if (
                item.active === false
              ) {
                return false;
              }

              if (!item.endDate) {
                return true;
              }

              const end =
                parseDate(
                  item.endDate
                );

              return (
                !end ||
                end >= today
              );
            })
            .sort((a, b) => {
              return (
                new Date(
                  a.startDate ||
                    "2999-01-01"
                ) -
                new Date(
                  b.startDate ||
                    "2999-01-01"
                )
              );
            })
            .slice(0, 3);

        setOtherShows(
          otherRows
        );
      } catch (error) {
        console.error(
          "Could not load tradeshow:",
          error
        );

        setShow(null);
      } finally {
        setLoading(false);
      }
    };

    loadShow();
  }, [slug]);


  /* ===========================
     PRODUCT SLIDER RESPONSIVE
     =========================== */

  useEffect(() => {
    const updateLayout = () => {
      if (
        window.innerWidth <= 768
      ) {
        setProductsPerView(1);
      } else if (
        window.innerWidth <= 1100
      ) {
        setProductsPerView(2);
      } else {
        setProductsPerView(3);
      }
    };

    updateLayout();

    window.addEventListener(
      "resize",
      updateLayout
    );

    return () =>
      window.removeEventListener(
        "resize",
        updateLayout
      );
  }, []);


  const products =
    show?.featuredProducts || [];

  const specialEvents =
    show?.specialEvents || [];


  const maximumProductIndex =
    useMemo(
      () =>
        Math.max(
          products.length -
            productsPerView,
          0
        ),
      [
        products.length,
        productsPerView,
      ]
    );


  useEffect(() => {
    if (
      productIndex >
      maximumProductIndex
    ) {
      setProductIndex(
        maximumProductIndex
      );
    }
  }, [
    productIndex,
    maximumProductIndex,
  ]);


  const goToPreviousProduct =
    () => {
      setProductIndex(
        (current) =>
          Math.max(
            current - 1,
            0
          )
      );
    };


  const goToNextProduct =
    () => {
      setProductIndex(
        (current) =>
          Math.min(
            current + 1,
            maximumProductIndex
          )
      );
    };


  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
      </div>
    );
  }


  if (!show) {
    return (
      <h1>
        Event not found.
      </h1>
    );
  }


  const boothText =
    formatBooth(show.booth);

  const formattedDates =
    formatTradeshowDates(
      show.startDate,
      show.endDate
    );

    const socialTitle = `Visit Go!Foton at ${show.name}${formattedDates ? ` | ${formattedDates}` : ""}`;

const socialDescription =
  show.heroDescription ||
  `Join Go!Foton at ${show.name}${show.location ? ` in ${show.location}` : ""}${formattedDates ? `, ${formattedDates}` : ""}.`;

const socialImage =
  show.backgroundImageUrl ||
  "https://gofoton.com/images/hometile1.jpg";

const socialUrl =
  `https://www.gofoton.com/${show.slug || show.id}`;

  const overviewHtml =
    DOMPurify.sanitize(
      show.overviewHtml || ""
    );


return (
  <div className="contact-page ise-page">
    <Helmet>
      <title>{socialTitle}</title>
      <meta name="description" content={socialDescription} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:image" content={socialImage} />
      <meta property="og:url" content={socialUrl} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={socialDescription} />
      <meta name="twitter:image" content={socialImage} />

      <link rel="canonical" href={socialUrl} />
    </Helmet>
      {/* ===========================
          MARQUEE
          =========================== */}

      <div className="single-marquee">
        <div className="single-marquee-slide">

          <video
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            src=""
            poster={
              show.backgroundImageUrl
            }
            autoPlay
            loop
            muted
            playsInline
          />


          <div className="single-overlay-content">

            <h1>
              {show.heroHeading ||
                show.name}
            </h1>


            <h2>
              {show.heroEventLine ||
                `Join us at ${show.name}`}
            </h2>


            {show.heroDescription && (
              <p>
                {
                  show.heroDescription
                }
              </p>
            )}


            {show.heroButtonLink && (
              <a
                href={
                  show.heroButtonLink
                }
                className="cta-button"
              >
                {show.heroButtonLabel ||
                  "Learn More"}
              </a>
            )}

          </div>


          <div className="ise-expo-logo-wrapper">

            {show.logoUrl && (
              <img
                className="ise-expo-logo"
                src={show.logoUrl}
                alt={show.name}
              />
            )}


            <div className="ise-expo-booth">
              {boothText}
            </div>

          </div>

        </div>
      </div>


      {/* ===========================
          CURVED DIVIDER
          =========================== */}

      <svg
        className="section-divider"
        viewBox="0 0 1440 150"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient
            id="tradeshowWhiteSplit"
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop
              offset="50%"
              stopColor="white"
              stopOpacity="1"
            />
          </linearGradient>
        </defs>

        <path
          d="
            M0,150
            H1440
            V0
            Q720,75 0,0
            Z
          "
          fill="url(#tradeshowWhiteSplit)"
        />
      </svg>


      {/* ===========================
          OVERVIEW
          =========================== */}

      <section className="trade-show-overview">

        <aside className="trade-show-event-badge">

          {show.eventWebsite ? (
            <a
              href={
                show.eventWebsite
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              {show.logoUrl && (
                <img
                  src={show.logoUrl}
                  alt={show.name}
                />
              )}
            </a>
          ) : (
            show.logoUrl && (
              <img
                src={show.logoUrl}
                alt={show.name}
              />
            )
          )}


          <strong>
            {show.overviewBadgeHeading ||
              `${show.name}${
                show.booth
                  ? ` Exhibit ${show.booth}`
                  : ""
              }`}
          </strong>


          <span>
            {formattedDates}
          </span>


          {show.venue && (
            <span>
              {show.venue}
            </span>
          )}


          <span>
            {show.location ||
              "Location Coming Soon"}
          </span>

        </aside>


        <div className="trade-show-overview-text">

          {overviewHtml ? (
            <div
              dangerouslySetInnerHTML={{
                __html:
                  overviewHtml,
              }}
            />
          ) : (
            <p>
              {show.heroDescription}
            </p>
          )}


          {(show.passText ||
            show.passLinkText) && (
            <p>
              {show.passText}{" "}

              {show.passLink && (
                <a
                  href={
                    show.passLink
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {show.passLinkText ||
                    "Register here"}
                </a>
              )}
            </p>
          )}

        </div>

      </section>


      {/* ===========================
          SPECIAL EVENTS / SCHEDULE
          =========================== */}

      {specialEvents.length > 0 && (
        <section className="trade-show-section">

          <h2 className="trade-show-section-title">
            {show.specialEventsHeading ||
              `Don’t miss these special opportunities at ${show.name}`}
          </h2>


          <div className="trade-show-special-events">

            {specialEvents.map(
              (event, index) => (
                <article
                  className="trade-show-special-card"
                  key={`${event.title}-${index}`}
                >

                  {event.image && (
                    <img
                      src={
                        event.image
                      }
                      alt={
                        event.title ||
                        ""
                      }
                    />
                  )}


                  <div className="trade-show-special-card-content">

                    {event.title && (
                      <h3>
                        {
                          event.title
                        }
                      </h3>
                    )}


                    {event.subtitle && (
                      <h4>
                        {
                          event.subtitle
                        }
                      </h4>
                    )}


                    {event.description && (
                      <p className="trade-show-special-description">
                        {
                          event.description
                        }
                      </p>
                    )}


                    {event.details
                      ?.length > 0 && (
                      <div className="trade-show-special-details">

                        {event.details.map(
                          (
                            detail,
                            detailIndex
                          ) => (
                            <span
                              key={
                                detailIndex
                              }
                            >
                              {detail}
                            </span>
                          )
                        )}

                      </div>
                    )}


                    {(event.buttonLabel ||
                      event.secondaryButtonLabel) && (
                      <div className="trade-show-card-buttons">

                        {event.buttonLabel &&
                          event.calendarLink && (
                          <a
                            className="trade-show-small-button"
                            href={
                              event.calendarLink
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {
                              event.buttonLabel
                            }
                          </a>
                        )}


                        {event.secondaryButtonLabel &&
                          event.secondaryButtonLink && (
                          <a
                            className="trade-show-small-button secondary"
                            href={
                              event.secondaryButtonLink
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {
                              event.secondaryButtonLabel
                            }
                          </a>
                        )}

                      </div>
                    )}

                  </div>

                </article>
              )
            )}

          </div>

        </section>
      )}


      {/* ===========================
          FEATURED PRODUCTS
          =========================== */}

      {products.length > 0 && (
        <section className="trade-show-section trade-show-products-section">

          <h2 className="trade-show-section-title">
            {show.featuredProductsHeading ||
              "Get a hands-on look at our featured optical connectivity"}
          </h2>


          <div className="trade-show-product-slider">

            {products.length >
              productsPerView && (
              <button
                type="button"
                className="trade-show-slider-arrow"
                onClick={
                  goToPreviousProduct
                }
                disabled={
                  productIndex === 0
                }
                aria-label="Previous products"
              >
                <IoChevronBack />
              </button>
            )}


            <div className="trade-show-product-viewport">

              <div
                className="trade-show-product-track"
                style={{
                  transform:
                    `translateX(-${
                      productIndex *
                      (100 /
                        productsPerView)
                    }%)`,

                  "--trade-products-per-view":
                    productsPerView,
                }}
              >

                {products.map(
                  (
                    product,
                    index
                  ) => (
                    <div
                      className="trade-show-product-slide"
                      key={`${product.name}-${index}`}
                    >

                      <a
                        className="trade-show-product-card"
                        href={
                          product.link
                        }
                      >

                        {product.image && (
                          <img
                            src={
                              product.image
                            }
                            alt={
                              product.name
                            }
                          />
                        )}


                        <div className="trade-show-product-name">
                          {
                            product.name
                          }
                        </div>


                        <span className="cta-button">
                          View
                        </span>

                      </a>

                    </div>
                  )
                )}

              </div>

            </div>


            {products.length >
              productsPerView && (
              <button
                type="button"
                className="trade-show-slider-arrow"
                onClick={
                  goToNextProduct
                }
                disabled={
                  productIndex ===
                  maximumProductIndex
                }
                aria-label="Next products"
              >
                <IoChevronForward />
              </button>
            )}

          </div>

        </section>
      )}


      {/* ===========================
          SUBSCRIBE
          =========================== */}

      {show.subscribeEnabled !==
        false && (
        <section className="trade-show-subscribe">

          <h2>
            {show.subscribeHeading ||
              `Can’t make ${show.name}? Let’s stay in touch.`}
          </h2>


          <a
            className="cta-button"
            href={
              show.subscribeButtonLink ||
              "/subscribe"
            }
          >
            {show.subscribeButtonLabel ||
              "Subscribe to Go!Foton news"}
          </a>

        </section>
      )}


      {/* ===========================
          OTHER UPCOMING SHOWS
          =========================== */}

      {show.otherShowsEnabled !==
        false &&
        otherShows.length > 0 && (
        <section className="trade-show-section">

          <h2 className="trade-show-section-title">
            {show.otherShowsHeading ||
              "Other upcoming shows"}
          </h2>


          <div className="trade-show-other-grid">

            {otherShows.map(
              (otherShow) => (
                <article
                  className="trade-show-other-card"
                  key={
                    otherShow.id
                  }
                >

                  <h3>
                    {
                      otherShow.name
                    }
                  </h3>

                  <p>
                    {otherShow.location ||
                      "Location Coming Soon"}
                  </p>

                  <p>
                    {formatTradeshowDates(
                      otherShow.startDate,
                      otherShow.endDate
                    )}
                  </p>


                  <a
                    href={`/${
                      otherShow.slug ||
                      otherShow.id
                    }`}
                    className="cta-button"
                  >
                    View Event
                  </a>

                </article>
              )
            )}

          </div>

        </section>
      )}


      {/* ===========================
          MOBILE DEMO / OTHER WAY
          TO FIND US
          =========================== */}

      {show.mobileUnitEnabled !==
        false && (
        <section className="trade-show-mobile-unit">

          <h2>
            {show.mobileUnitHeading ||
              "Need us to come to you?"}
          </h2>


          <a
            className="cta-button"
            href={
              show.mobileUnitButtonLink ||
              "mailto:brand@gofoton.com?subject=I%E2%80%99d%20like%20to%20request%20a%20Go!Foton%20mobile%20demo"
            }
          >
            {show.mobileUnitButtonLabel ||
              "Request the Go!Foton mobile demo unit"}
          </a>

        </section>
      )}

    </div>
  );
}

export default Tradeshow;